/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';
import { usbFeature, UsbState } from './usb.reducer';

export const selectUsbState = createFeatureSelector<UsbState>(usbFeature.name);
export const selectUsbEffectInitialized = createSelector(selectUsbState, (state) => state.effectsInitialized);

export const selectSamAttached = createSelector(selectUsbState, (state) => state.samAttached);
export const selectRtiAttached = createSelector(selectUsbState, (state) => state.rtiAttached);

export const selectUsbAttached = createSelector(selectSamAttached, selectRtiAttached, (samAttached, rtiAttached) => samAttached || rtiAttached);

export const selectRtiInitialized = createSelector(selectUsbState, (state) => state.rtiInitialized);
export const selectSamInitialized = createSelector(selectUsbState, (state) => state.samInitialized);

export const selectUsbAttachedAndInitialized = createSelector(selectUsbAttached, selectRtiInitialized, selectSamInitialized, (attached, rtiInitialized, samInitialized) => attached && (rtiInitialized || samInitialized));

export const selectUsbFirmwareChecked = createSelector(selectUsbState, (state) => Boolean(state.firmwareChecked));

export const selectUsbDeviceName = createSelector(selectSamAttached, selectRtiAttached, (samAttached, rtiAttached) => samAttached ? 'SAM' : rtiAttached ? 'RTI' : 'Unknown');

export const selectUsbHardware = createSelector(selectUsbState, (state) => state.hardware);
export const selectUsbFirmware = createSelector(selectUsbState, (state) => state.firmware);
export const selectUsbSerial = createSelector(selectUsbState, (state) => state.serial);

export const selectConnectedUsbCallAudioInputs = createSelector(selectUsbState, (state) => state.enforceHeadsetPresence ? state.inputs.filter((input) => input.connected) : state.inputs);

export const selectConnectedUsbCallAudioOutputs = createSelector(selectUsbState, (state) => state.enforceHeadsetPresence ? state.outputs.filter((input) => input.connected) : state.outputs);

export const selectMuteOnAnswerUsbInputs = createSelector(selectConnectedUsbCallAudioInputs, (inputs) => inputs.filter((input) => input.muteOnAnswer));

export const selectUsbAlertOutputToDevice = createSelector(selectUsbState, (state) => state.alertToDevice);

export const selectUsbAlertOutputToHeadsets = createSelector(selectUsbState, (state) => state.alertToHeadsets);

export const selectUsbIrrOutputToDevice = createSelector(selectUsbState, (state) => state.irrToDevice);

export const selectUsbIrrOutputToHeadsets = createSelector(selectUsbState, (state) => state.irrToHeadsets);

export const selectUsbInputs = createSelector(selectUsbState, (state) => state.inputs);

export const selectEnforceHeadsetPresence = createSelector(selectUsbState, (state) => state.enforceHeadsetPresence);

export const selectUsbJackSense = createSelector(selectUsbState, (state) => state.jackSense);

export const selectUsbHeadset1Connected = createSelector(selectUsbJackSense, (jackSense) => Boolean(jackSense['JACKBOX_1']));

export const selectUsbHeadset2Connected = createSelector(selectUsbJackSense, (jackSense) => Boolean(jackSense['JACKBOX_2']));

export const selectUsbRadioHeadsetConnected = createSelector(selectUsbJackSense, (jackSense) => Boolean(jackSense['RADIO_CONSOLE']));

export const selectUsbHeadsetConnected = createSelector(
    selectUsbHeadset1Connected,
    selectUsbHeadset2Connected,
    selectUsbRadioHeadsetConnected,
    selectEnforceHeadsetPresence,
    (headset1Connected, headset2Connected, radioHeadsetConnected, enforce) => (headset1Connected || headset2Connected || radioHeadsetConnected) || !enforce
);

export const selectUsbDisconnectedAndTimedOut = createSelector(selectUsbState, (state) => state.headsetDisconnectedAndTimedOut);

export const selectIsUsbAttachedAndHeadsetDisconnected = createSelector(
    selectUsbAttached,
    selectUsbHeadsetConnected,
    (usbAttached, headsetConnected) => usbAttached && !headsetConnected
);

export const selectUsbVolumeByDeviceId = (deviceId: string) => createSelector(
    selectUsbState,
    (state) => state.volume[deviceId] ?? 0
);

